import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  BellOff,
  CheckCircle2,
  Download,
  Mail,
  MailOpen,
  MessageSquareWarning,
  MousePointerClick,
  Reply,
  Send,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import {
  EMAIL_COMPACT_METRIC_DEFS,
  EMAIL_DETAIL_TABS,
  EMAIL_METRIC_CARD_DEFS,
  EMAIL_RECIPIENT_TABLE_HEADERS,
  buildEmailMetricCards,
  buildEmailStatsCsv,
  buildEngagementSummary,
  buildTimelineBuckets,
  defaultDateRange,
  downloadEmailStatsCsv,
  filterRecipientsByTab,
  formatChartAxisDate,
  formatCount,
  normalizeEmailStats,
} from '../../utils/emailStatisticsUtils';

const METRIC_ICONS = {
  total: Send,
  delivered: CheckCircle2,
  opened: MailOpen,
  clicked: MousePointerClick,
  replied: Reply,
  bounced: MessageSquareWarning,
  unsubscribed: BellOff,
  accepted: ThumbsUp,
  rejected: XCircle,
  complained: AlertTriangle,
  softBounced: MessageSquareWarning,
  hardBounced: MessageSquareWarning,
  failed: XCircle,
  spam: AlertTriangle,
};

function DashboardSkeleton({ compact = false }) {
  return (
    <div
      className={cn('animate-pulse space-y-4 p-4', compact && 'space-y-3')}
      data-testid="email-statistics-dashboard-skeleton"
    >
      {Array.from({ length: compact ? 7 : 4 }).map((_, index) => (
        <div key={index} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-20 rounded bg-slate-200" />
              <div className="h-6 w-16 rounded bg-slate-200" />
            </div>
          </div>
        </div>
      ))}
      {!compact ? (
        <>
          <div className="h-40 rounded-xl border border-slate-200 bg-slate-100" />
          <div className="h-48 rounded-xl border border-slate-200 bg-slate-100" />
        </>
      ) : null}
    </div>
  );
}

function MetricCard({ card, onViewDetails, compact }) {
  const Icon = METRIC_ICONS[card.id] || Mail;
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={`email-stat-card-${card.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-500">{card.label}</p>
          <p className="text-2xl font-semibold text-slate-900">{card.value}</p>
        </div>
      </div>
      {card.viewDetails ? (
        <button
          type="button"
          className="mt-3 border-t border-slate-100 pt-3 text-sm font-medium text-blue-600 hover:underline"
          data-testid={`email-stat-view-details-${card.id}`}
          onClick={() => onViewDetails?.(card.id)}
        >
          View details
        </button>
      ) : null}
      {compact && card.id === 'unsubscribed' ? null : null}
    </div>
  );
}

function EngagementChart({ data, dateRange }) {
  const chartRows = useMemo(
    () => data.map((row) => ({
      ...row,
      label: formatChartAxisDate(row.date),
    })),
    [data],
  );

  const yMax = Math.max(
    10,
    ...chartRows.flatMap((row) => [row.delivered, row.opened, row.clicked]),
  );

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="email-statistics-engagement-chart"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-sm font-semibold text-slate-900">Engagement Analysis</h5>
        <div
          className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
          data-testid="email-statistics-date-range"
        >
          {dateRange.start} → {dateRange.end}
        </div>
      </div>
      <div className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ borderRadius: 8, borderColor: '#e2e8f0', fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area
              type="monotone"
              dataKey="delivered"
              name="Delivered"
              stroke="#22c55e"
              fill="#22c55e33"
              strokeWidth={2}
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="opened"
              name="Opened"
              stroke="#3b82f6"
              fill="#3b82f633"
              strokeWidth={2}
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="clicked"
              name="Clicked"
              stroke="#f59e0b"
              fill="#f59e0b22"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DetailsTable({
  recipients,
  detailTab,
  onDetailTabChange,
  onExport,
  pageSize = 10,
}) {
  const [page, setPage] = useState(0);
  const filtered = useMemo(
    () => filterRecipientsByTab(recipients, detailTab),
    [detailTab, recipients],
  );
  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, pageCount - 1);
  const start = total === 0 ? 0 : safePage * pageSize;
  const end = total === 0 ? 0 : Math.min(total, start + pageSize);
  const pageRows = filtered.slice(start, end);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="email-statistics-details"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <h5 className="text-sm font-semibold text-slate-900">Details</h5>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={onExport}
          data-testid="email-statistics-export"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </div>

      <div
        className="flex gap-1 overflow-x-auto border-b border-slate-200 px-2"
        data-testid="email-statistics-detail-tabs"
      >
        {EMAIL_DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              onDetailTabChange(tab.id);
              setPage(0);
            }}
            className={cn(
              'whitespace-nowrap border-b-2 px-3 py-2 text-xs font-medium transition-colors',
              detailTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
            data-testid={`email-statistics-detail-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto" data-testid="email-statistics-recipient-table">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {EMAIL_RECIPIENT_TABLE_HEADERS.map((header) => (
                <th key={header} className="px-4 py-3 font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? (
              pageRows.map((row, index) => (
                <tr
                  key={row.id || row.email || index}
                  className="border-t border-slate-100 text-slate-700"
                >
                  <td className="px-4 py-3 text-blue-600">
                    {row.full_name || row.fullName || row.name || '—'}
                  </td>
                  <td className="px-4 py-3 text-blue-600">
                    {row.email || row.recipient || '—'}
                  </td>
                  <td className="px-4 py-3">{row.status || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {row.last_updated || row.updated_at || row.updated || '—'}
                  </td>
                </tr>
              ))
            ) : (
              <tr data-testid="email-statistics-empty-recipients-row">
                <td
                  colSpan={EMAIL_RECIPIENT_TABLE_HEADERS.length}
                  className="px-4 py-10 text-center text-sm text-slate-500"
                >
                  No email activity for this workflow yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-xs text-slate-500"
        data-testid="email-statistics-pagination"
      >
        <span>
          Showing {total === 0 ? 0 : start + 1} - {end} of {total} results
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage <= 0 || total === 0}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={end >= total || total === 0}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {'compact'|'full'} variant
 * compact = Send Email action Statistics tab
 * full = detail modal / expanded analytics
 */
const EmailStatisticsDashboard = ({
  stats = null,
  isLoading = false,
  error = null,
  onRetry,
  trackingReady = true,
  variant = 'full',
  className,
  onOpenDetails,
}) => {
  const compact = variant === 'compact';
  const [showNumbers, setShowNumbers] = useState(false);
  const [mainTab, setMainTab] = useState('summary');
  const [detailTab, setDetailTab] = useState('delivered');
  const [dateRange] = useState(() => defaultDateRange(7));

  const normalized = useMemo(() => normalizeEmailStats(stats), [stats]);
  const metricCards = useMemo(
    () => buildEmailMetricCards(normalized, {
      showNumbers: compact ? false : showNumbers,
      defs: (compact ? EMAIL_COMPACT_METRIC_DEFS : EMAIL_METRIC_CARD_DEFS).map((def) => ({
        ...def,
        viewDetails: compact
          ? true
          : Boolean(EMAIL_METRIC_CARD_DEFS.find((item) => item.id === def.id)?.viewDetails),
      })),
    }),
    [compact, normalized, showNumbers],
  );
  const engagement = useMemo(
    () => buildEngagementSummary(normalized, { showNumbers }),
    [normalized, showNumbers],
  );
  const chartData = useMemo(
    () => buildTimelineBuckets(normalized, dateRange),
    [dateRange, normalized],
  );

  const handleExport = () => {
    const csv = buildEmailStatsCsv(normalized, normalized.recipients, detailTab);
    downloadEmailStatsCsv('workflow-email-statistics.csv', csv);
  };

  const handleViewDetails = (metricId) => {
    if (typeof onOpenDetails === 'function') {
      onOpenDetails(metricId || 'delivered');
      return;
    }
    setMainTab('summary');
    setDetailTab(metricId || 'delivered');
  };

  if (isLoading) {
    return <DashboardSkeleton compact={compact} />;
  }

  if (compact) {
    return (
      <div
        className={cn('space-y-3 p-4', className)}
        data-testid="email-statistics-dashboard"
        data-variant="compact"
        data-workflow-status-gated="false"
      >
        {error ? (
          <div
            className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="email-statistics-error"
          >
            <span>Statistics could not be refreshed.</span>
            {onRetry ? (
              <button type="button" className="font-medium underline" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-3" data-testid="email-statistics-metric-cards">
          {metricCards.map((card) => (
            <MetricCard
              key={card.id}
              card={{ ...card, viewDetails: true }}
              onViewDetails={handleViewDetails}
              compact
            />
          ))}
        </div>

        {!normalized.total ? (
          <p
            className="text-xs text-slate-500"
            data-testid="email-statistics-empty-note"
          >
            This email has not recorded any activity yet.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-5 p-4', className)}
      data-testid="email-statistics-dashboard"
      data-variant="full"
      data-workflow-status-gated="false"
    >
      {!trackingReady ? (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="email-statistics-tracking-banner"
        >
          Mailgun tracking is not fully configured. Metrics will stay at zero until tracking is ready.
        </div>
      ) : null}

      {error ? (
        <div
          className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="email-statistics-error"
        >
          <span>Statistics could not be refreshed.</span>
          {onRetry ? (
            <button type="button" className="font-medium underline" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex border-b border-slate-200" data-testid="email-statistics-main-tabs">
        {[
          { id: 'summary', label: 'Summary' },
          { id: 'click_performance', label: 'Click Performance' },
          { id: 'tags', label: 'Tags' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setMainTab(tab.id)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium',
              mainTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
            data-testid={`email-statistics-main-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {mainTab === 'summary' ? (
        <>
          <div
            className="rounded-xl border border-slate-200 bg-white p-4"
            data-testid="email-statistics-engagement-summary"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h5 className="text-sm font-semibold text-slate-900">Engagement Summary</h5>
              <div className="flex items-center gap-2">
                <Switch
                  id="email-stats-show-numbers"
                  checked={showNumbers}
                  onCheckedChange={setShowNumbers}
                  data-testid="email-statistics-show-numbers"
                />
                <Label htmlFor="email-stats-show-numbers" className="text-sm text-slate-600">
                  Show Stats in Numbers
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {engagement.map((row) => (
                <div
                  key={row.id}
                  className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3"
                  data-testid={`email-engagement-${row.id}`}
                >
                  <p className="text-xs text-slate-500">{row.label}</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{row.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="email-statistics-metric-cards">
            {metricCards.map((card) => (
              <MetricCard key={card.id} card={card} onViewDetails={handleViewDetails} />
            ))}
          </div>

          <EngagementChart data={chartData} dateRange={dateRange} />

          <DetailsTable
            recipients={normalized.recipients}
            detailTab={detailTab}
            onDetailTabChange={setDetailTab}
            onExport={handleExport}
          />
        </>
      ) : (
        <div
          className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500"
          data-testid={`email-statistics-${mainTab}-placeholder`}
        >
          {mainTab === 'click_performance'
            ? `No click performance data yet. Total clicks: ${formatCount(normalized.clicked)}.`
            : `No tags recorded yet.`}
        </div>
      )}
    </div>
  );
};

export function EmailStatisticsDetailsModal({
  isOpen,
  onClose,
  stats,
  isLoading,
  error,
  onRetry,
}) {
  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      data-testid="email-statistics-details-modal"
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-slate-100 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">Email</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
            data-testid="email-statistics-details-close"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto">
          <EmailStatisticsDashboard
            variant="full"
            stats={stats}
            isLoading={isLoading}
            error={error}
            onRetry={onRetry}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default EmailStatisticsDashboard;
